/**
 * ErrorBoundary Component
 * Catches JavaScript errors in child components and displays fallback UI
 * Follows TribunalReady design patterns from FORM_BUILDER_REFERENCE_v2.md
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Optional callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Show detailed error info (dev mode only) */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// =============================================================================
// DEFAULT FALLBACK UI
// =============================================================================

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  showDetails?: boolean;
}

const ErrorFallbackUI: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onReset,
  showDetails = false,
}) => {
  const isDev = import.meta.env.DEV;
  const shouldShowDetails = showDetails || isDev;

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6" role="alert" aria-live="assertive">
      <div className="max-w-lg w-full">
        {/* Error Card */}
        <div className="bg-slate-900 border border-red-500/30 rounded-xl p-6 shadow-xl">
          {/* Icon + Title */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-6 h-6 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">
                Something went wrong
              </h2>
              <p className="text-sm text-slate-400">
                An unexpected error occurred. Your data is safe.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-400 font-mono break-words">
                {error.message || 'Unknown error'}
              </p>
            </div>
          )}

          {/* Stack Trace (Dev Mode) */}
          {shouldShowDetails && errorInfo && (
            <details className="mb-6">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400 transition-colors">
                Technical Details (Dev Mode)
              </summary>
              <pre className="mt-2 p-3 bg-slate-800/50 rounded-lg text-xs text-slate-400 overflow-auto max-h-48 font-mono">
                {errorInfo.componentStack}
              </pre>
            </details>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onReset}
              className="tr-btn tr-btn-primary flex-1 justify-center"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="tr-btn tr-btn-secondary flex-1 justify-center"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Reload Page
            </button>
          </div>

          {/* Help Link */}
          <p className="text-xs text-slate-500 mt-4 text-center">
            If this keeps happening, please{' '}
            <a
              href="mailto:support@tribunalready.com"
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// ERROR BOUNDARY CLASS COMPONENT
// =============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console (will also go to CloudWatch if Lambda logging)
    console.error('[ErrorBoundary] Caught error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Use default fallback UI
      return (
        <ErrorFallbackUI
          error={error}
          errorInfo={errorInfo}
          onReset={this.handleReset}
          showDetails={showDetails}
        />
      );
    }

    return children;
  }
}

// =============================================================================
// CONVENIENCE HOOK FOR FUNCTIONAL ERROR HANDLING
// =============================================================================

/**
 * Hook to manually trigger error boundary from within a component
 * Usage: const throwError = useErrorHandler(); throwError(new Error('...'));
 */
export const useErrorHandler = (): ((error: Error) => void) => {
  const [, setError] = React.useState<Error | null>(null);

  return React.useCallback((error: Error) => {
    setError(() => {
      throw error;
    });
  }, []);
};

export default ErrorBoundary;
